<template>
  <footer>
    <PageFooterBanners :is-error-layout="isErrorLayout" />
    <div class="inner-footer">
      <div class="footer-nav-and-social">
        <nav v-if="footerMenu?.length > 0" class="footer-nav-wrapper" role="navigation">
          <ul class="footer-nav">
            <li v-for="item in footerMenu" :key="item.label" class="footer-nav-menu-item-link">
              <component
                :is="item.url.includes('.rss') ? 'a' : NuxtLink"
                :to="item.url.includes('.rss') ? undefined : item.url"
                :href="item.url.includes('.rss') ? withBase(item.url, baseURL) : undefined"
                :target="item.external ? '_blank' : '_self'"
                :external="isErrorLayout"
                class="footer-nav-menu-item-link-inner"
                @click="handleLinkClick(item.label, item.url)"
                @click.middle="handleLinkClick(item.label, item.url)"
              >
                <span class="footer-nav-menu-item-link-label">
                  {{ item.label }}
                </span>
              </component>
            </li>
          </ul>

          <ul v-if="broadcasts?.length > 0" class="footer-nav-broadcasts">
            <li
              v-for="broadcast in broadcasts"
              :key="broadcast.title"
              class="footer-nav-broadcasts-item"
            >
              <Icon
                :name="broadcast.icon"
                class="footer-nav-broadcasts-item-icon"
                role="presentation"
              />
              <span>
                {{ broadcast.title }}
              </span>
            </li>
          </ul>
        </nav>
        <Socialkit :analytics="{ linkAnalyticsTag: 'footer' }" />

        <p class="about-us">
          EenVandaag is de onafhankelijke nieuwsredactie van publieke omroep
          <a href="https://www.avrotros.nl" target="_blank" rel="noopener">AVROTROS</a>.
        </p>
      </div>

      <div v-if="brands.length > 0" class="footer-brand-bar">
        <template v-for="brand in brands" :key="brand.title">
          <NuxtLink
            v-if="brand.url"
            :to="brand.url"
            :external="isErrorLayout"
            target="_blank"
            :aria-label="brand.title"
            @click="handleLinkClick(brand.title, brand.url)"
            @click.middle="handleLinkClick(brand.title, brand.url)"
          >
            <Logo v-if="brand.title === 'AVROTROS'" class="footer-brand-logo-at" />
            <Icon v-else :name="brand.icon" class="footer-brand-logo" role="presentation" />
          </NuxtLink>
        </template>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { withBase } from 'ufo';
import siteConfig from '~~/configs/site.config';
import useGlobalConfigStore from '~/stores/global';
import { NuxtLink } from '#components';

const baseURL = useRuntimeConfig().app.baseURL || '/';
const brands = siteConfig?.logos || [];
const broadcasts = siteConfig?.footer?.broadcasts || [];

const globalConfigStore = useGlobalConfigStore();
const { footerMenu } = globalConfigStore;

const handleLinkClick = (linkText: string, linkUrl: string) => {
  gtmTrackEvent({
    linkAnalyticsTag: 'footer',
    linkText,
    linkUrl,
  });
};

defineProps({
  isErrorLayout: {
    type: Boolean,
    default: false,
  },
});
</script>

<style scoped lang="postcss">
.inner-footer {
  background-color: hsl(var(--color-primary-950));
  position: relative;
  z-index: 10;
}

.footer-nav-and-social {
  --footer-nav-and-social-padding: calc(var(--base-spacing) * 9);
  --footer-nav-and-social-gap: calc(var(--base-spacing) * 8);

  flex-direction: column;
  display: flex;
  padding: var(--footer-nav-and-social-padding);
  gap: var(--footer-nav-and-social-gap);

  @media (--lg-viewport) {
    --footer-nav-and-social-padding: calc(var(--base-spacing) * 12) calc(var(--base-spacing) * 9);
    --footer-nav-and-social-gap: calc(var(--base-spacing) * 12);
  }
}

.footer-nav-wrapper {
  --footer-nav-wrapper-gap: calc(var(--base-spacing) * 8);

  margin: 0 auto;
  width: 100%;
  max-width: var(--global-site-width);
  display: flex;
  flex-direction: column-reverse;
  color: hsl(var(--color-neutral-white));
  gap: var(--footer-nav-wrapper-gap);

  @media (--lg-viewport) {
    --footer-nav-wrapper-gap: auto;

    flex-direction: row;
    justify-content: space-between;
  }
}

.footer-nav {
  --footer-nav-gap: calc(var(--base-spacing) * 2);

  display: grid;
  width: 100%;

  @media (--lg-viewport) {
    gap: var(--footer-nav-gap);
    max-width: 50%;
    grid-template-columns: 50% 50%;
  }
}

.footer-nav-menu-item-link {
  --footer-nav-menu-item-height: calc(var(--base-spacing) * 11);
  height: auto;
  display: inline-flex;
  align-items: center;
  min-height: var(--footer-nav-menu-item-height);

  @mixin title-md {
  }
}

.footer-nav-menu-item-link-inner {
  transition: box-shadow 250ms ease-in-out;
  padding-right: var(--unit-12);
  height: 100%;
  display: flex;
  align-items: center;

  @add-mixin hover {
    color: hsl(var(--color-primary-300));
  }

  &:active {
    color: hsl(var(--color-primary-400));
  }

  @add-mixin focus dark {
    color: hsl(var(--color-primary-300));
  }
}

.footer-nav-menu-item-link-label {
  transition: color 200ms ease-in-out;
}

.footer-nav-broadcasts {
  --footer-nav-broadcasts-padding: 0;
  --footer-nav-broadcasts-gap: calc(var(--base-spacing) * 5);
  --footer-nav-broadcasts-max-width: 312px;

  display: flex;
  flex-direction: column;
  gap: var(--footer-nav-broadcasts-gap);
  padding: var(--footer-nav-broadcasts-padding);

  @media (--lg-viewport) {
    --footer-nav-broadcasts-padding: calc(var(--base-spacing) * 1.5) 0;
    max-width: var(--footer-nav-broadcasts-max-width);
  }
}

.footer-nav-broadcasts-item {
  --footer-nav-broadcasts-item-gap: calc(var(--base-spacing) * 3);

  display: inline-flex;
  align-items: center;
  gap: var(--footer-nav-broadcasts-item-gap);

  color: hsl(var(--color-primary-300));
  @mixin body-sm {
  }

  @media (--lg-viewport) {
    --footer-nav-broadcasts-item-gap: calc(var(--base-spacing) * 4);
  }
}

.footer-nav-broadcasts-item-icon {
  --footer-nav-broadcasts-item-icon-size: 36px;

  font-size: var(--footer-nav-broadcasts-item-icon-size);
  flex-shrink: 0;

  @media (--lg-viewport) {
    --footer-nav-broadcasts-item-icon-size: 44px;
  }
}

.footer-brand-bar {
  --footer-brand-bar-padding: calc(var(--base-spacing) * 6) calc(var(--base-spacing) * 4);
  --footer-brand-bar-gap: calc(var(--base-spacing) * 4);

  background-color: hsl(var(--color-at-primary));
  display: flex;
  align-items: center;
  justify-content: center;

  gap: var(--footer-brand-bar-gap);
  padding: var(--footer-brand-bar-padding);

  @media (--md-viewport) {
    --footer-brand-bar-padding: calc(var(--base-spacing) * 8) calc(var(--base-spacing) * 9);
    --footer-brand-bar-gap: calc(var(--base-spacing) * 6);
  }
}

.footer-brand-logo {
  --footer-brand-logo-size: 42px;

  font-size: var(--footer-brand-logo-size);

  @media (--lg-viewport) {
    --footer-brand-logo-size: 56px;
  }
}

.footer-brand-logo-at {
  --footer-brand-logo-size: 42px;

  height: var(--footer-brand-logo-size);

  @media (--lg-viewport) {
    --footer-brand-logo-size: 56px;
  }
}

.about-us {
  --margin-top: var(--unit-28);
  --text-align: left;

  display: block;
  text-align: var(--text-align);
  color: hsl(var(--color-primary-400));
  margin-top: var(--margin-top);

  @include body-sm;

  @media (--lg-viewport) {
    --margin-top: var(--unit-16);
    --text-align: center;
  }

  > a {
    transition: color 200ms ease;
    &:hover {
      color: hsl(var(--color-primary-500));
    }
  }
}
</style>
